// Open a new window and print an invoice using the configured template (A4 paper)
import { API_BASE } from "@/lib/api";
import { selectedServiceLabel } from "@/lib/packageLines";

const resolveImg = (u) => {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return API_BASE.replace(/\/api$/, "") + u;
};

const fmt = (n) => Number(n || 0).toLocaleString("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function formatPaymentLine(invoice, isAR) {
  const splits = invoice?.payment_splits;
  if (Array.isArray(splits) && splits.length) {
    const parts = splits.map((s) => `${s.payment_method || ""} ${Number(s.amount || 0).toFixed(2)}`);
    return isAR ? `مقسّم: ${parts.join(" + ")}` : `Split: ${parts.join(" + ")}`;
  }
  return invoice.payment_method || "";
}

export function printInvoice(invoice, template, lang = "ar") {
  const isAR = (template?.language || lang) === "ar";
  const dir = isAR ? "rtl" : "ltr";
  const t = (en, ar) => (isAR ? ar : en);

  const dateStr = (() => {
    try { return new Date(invoice.created_at).toLocaleString(isAR ? "ar-EG" : "en-US"); }
    catch { return invoice.created_at || ""; }
  })();

  const itemsRows = (invoice.items || []).map((it, idx) => {
    const lineSubtotal = (it.unit_price || 0) * (it.qty || 1);
    const lineDisc = Number(it.line_discount_amount || 0);
    const lineTotal = it.line_total != null ? Number(it.line_total) : (lineSubtotal - lineDisc);
    const subs = (it.selected_services || []).map((sel) => `
      <tr>
        <td class="num"></td>
        <td style="padding-inline-start:14px;font-size:10px;color:#666;">• ${escape(selectedServiceLabel(sel, isAR ? "ar" : "en"))}</td>
        <td class="num"></td>
        <td class="num"></td>
        <td class="num"></td>
      </tr>
    `).join("");
    return `
      <tr>
        <td class="num">${idx + 1}</td>
        <td>${escape(it.name || "")}${lineDisc > 0 ? `<div style="font-size:10px;color:#b91c1c;margin-top:2px">${escape(it.discount_reason_label || (isAR ? "خصم" : "Discount"))}: -${fmt(lineDisc)}</div>` : ""}</td>
        <td class="num">${it.qty}</td>
        <td class="num">${fmt(it.unit_price)}</td>
        <td class="num strong">${fmt(lineTotal)}</td>
      </tr>
      ${subs}
    `;
  }).join("");

  const primary = template?.primary_color || "#D4AF64";

  const html = `
<!DOCTYPE html>
<html lang="${isAR ? "ar" : "en"}" dir="${dir}">
<head>
<meta charset="UTF-8" />
<title>${escape(invoice.invoice_number || "")}</title>
<style>
  @page { size: A4; margin: 14mm; }
  * { box-sizing: border-box; }
  html, body {
    margin: 0; padding: 0;
    font-family: ${isAR ? "'IBM Plex Sans Arabic', sans-serif" : "'Inter', -apple-system, sans-serif"};
    color: #111;
    background: #fff;
    font-size: 11pt;
  }
  .page { width: 100%; max-width: 182mm; margin: 0 auto; padding: 8mm 0; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; padding-bottom: 12px; border-bottom: 2px solid ${primary}; }
  .brand { display: flex; gap: 12px; align-items: center; }
  .logo { width: 70px; height: 70px; object-fit: contain; }
  .biz h1 { margin: 0 0 4px 0; font-size: 20pt; color: ${primary}; }
  .biz .meta { font-size: 9pt; color: #555; line-height: 1.5; }
  .doc-info { text-align: ${isAR ? "left" : "right"}; }
  .doc-info .doc-title { font-size: 16pt; color: ${primary}; font-weight: 700; margin-bottom: 6px; }
  .doc-info .row { font-size: 10pt; color: #333; line-height: 1.6; }
  .doc-info .label { color: #777; margin-${isAR ? "left" : "right"}: 6px; }

  .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-top: 16px; }
  .info-box { border: 1px solid #ddd; padding: 10px 12px; border-radius: 4px; background: #fafafa; }
  .info-box h3 { margin: 0 0 6px 0; font-size: 9pt; color: #666; text-transform: uppercase; letter-spacing: 0.5px; }
  .info-box .v { font-size: 11pt; color: #111; line-height: 1.5; }

  table.items { width: 100%; border-collapse: collapse; margin-top: 18px; }
  table.items th { background: ${primary}; color: #fff; padding: 9px 10px; font-size: 10pt; text-align: ${isAR ? "right" : "left"}; font-weight: 600; }
  table.items td { padding: 9px 10px; font-size: 10.5pt; border-bottom: 1px solid #eee; }
  table.items td.num { text-align: ${isAR ? "left" : "right"}; font-variant-numeric: tabular-nums; }
  table.items td.strong { font-weight: 700; color: ${primary}; }
  table.items tr:nth-child(even) td { background: #fbfaf7; }

  .totals { margin-top: 12px; display: flex; justify-content: ${isAR ? "flex-start" : "flex-end"}; }
  .totals table { min-width: 280px; border-collapse: collapse; }
  .totals td { padding: 6px 10px; font-size: 11pt; }
  .totals .lbl { color: #555; }
  .totals .val { text-align: ${isAR ? "left" : "right"}; font-variant-numeric: tabular-nums; }
  .totals .grand { border-top: 2px solid ${primary}; font-weight: 700; font-size: 13pt; }
  .totals .grand .lbl { color: ${primary}; }
  .totals .grand .val { color: ${primary}; }

  .footer { margin-top: 28px; padding-top: 14px; border-top: 1px dashed #aaa; font-size: 10pt; color: #555; text-align: center; line-height: 1.6; }
  .footer .terms { font-size: 9pt; color: #666; margin-top: 6px; white-space: pre-line; }
  .footer .thanks { font-size: 12pt; color: ${primary}; font-weight: 600; }
  .actions { margin-top: 12px; text-align: center; padding-top: 14px; }
  .actions button { background: ${primary}; color: #fff; border: 0; padding: 10px 20px; border-radius: 6px; cursor: pointer; font-size: 11pt; }

  @media print {
    .actions { display: none; }
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
</style>
</head>
<body>
  <div class="page">
    <div class="header">
      <div class="brand">
        ${template?.logo_url ? `<img class="logo" src="${escape(resolveImg(template.logo_url))}" alt="" />` : ""}
        <div class="biz">
          <h1>${escape(isAR ? template?.business_name_ar : template?.business_name_en) || ""}</h1>
          <div class="meta">
            ${template?.business_address_ar || template?.business_address_en ? `<div>${escape(isAR ? template?.business_address_ar : template?.business_address_en)}</div>` : ""}
            ${template?.phone ? `<div>${t("Phone", "هاتف")}: ${escape(template.phone)}</div>` : ""}
            ${template?.email ? `<div>${escape(template.email)}</div>` : ""}
            ${template?.tax_number ? `<div>${t("VAT", "الرقم الضريبي")}: ${escape(template.tax_number)}</div>` : ""}
            ${template?.cr_number ? `<div>${t("CR", "س.ت")}: ${escape(template.cr_number)}</div>` : ""}
          </div>
        </div>
      </div>
      <div class="doc-info">
        <div class="doc-title">${escape(isAR ? template?.header_title_ar : template?.header_title_en) || (isAR ? "فاتورة" : "Invoice")}</div>
        <div class="row"><span class="label">${t("Invoice #", "رقم الفاتورة")}:</span> <strong>${escape(invoice.invoice_number || "")}</strong></div>
        <div class="row"><span class="label">${t("Date", "التاريخ")}:</span> ${dateStr}</div>
        <div class="row"><span class="label">${t("Payment", "طريقة الدفع")}:</span> ${escape(formatPaymentLine(invoice, isAR))}</div>
      </div>
    </div>

    ${(template?.show_customer || template?.show_phone) ? `
    <div class="info-grid">
      ${template?.show_customer ? `
        <div class="info-box">
          <h3>${t("Bill To", "فاتورة إلى")}</h3>
          <div class="v">${escape(invoice.customer_name || (isAR ? "زبون عابر" : "Walk-in"))}</div>
          ${template?.show_phone && invoice.customer_phone ? `<div class="v">${escape(invoice.customer_phone)}</div>` : ""}
        </div>
      ` : ""}
      ${template?.show_loyalty && invoice.loyalty_earned ? `
        <div class="info-box">
          <h3>${t("Loyalty", "نقاط الولاء")}</h3>
          <div class="v">${t("Earned", "اكتسبت")}: <strong>${invoice.loyalty_earned}</strong> ${t("points", "نقطة")}</div>
          ${invoice.loyalty_redeemed ? `<div class="v">${t("Redeemed", "استُبدلت")}: <strong>${invoice.loyalty_redeemed}</strong></div>` : ""}
        </div>
      ` : ""}
    </div>
    ` : ""}

    <table class="items">
      <thead>
        <tr>
          <th style="width:6%;">#</th>
          <th>${t("Item", "البند")}</th>
          <th style="width:10%;">${t("Qty", "الكمية")}</th>
          <th style="width:18%;">${t("Price", "السعر")}</th>
          <th style="width:18%;">${t("Total", "الإجمالي")}</th>
        </tr>
      </thead>
      <tbody>${itemsRows}</tbody>
    </table>

    <div class="totals">
      <table>
        <tr><td class="lbl">${t("Subtotal", "المجموع الفرعي")}</td><td class="val">${fmt(invoice.subtotal)}</td></tr>
        ${template?.show_discount && invoice.discount ? `<tr><td class="lbl">${t("Discount", "خصم")}</td><td class="val">-${fmt(invoice.discount)}</td></tr>` : ""}
        ${invoice.loyalty_value_applied ? `<tr><td class="lbl">${t("Loyalty Value", "قيمة النقاط")}</td><td class="val">-${fmt(invoice.loyalty_value_applied)}</td></tr>` : ""}
        ${template?.show_tax && invoice.tax ? `<tr><td class="lbl">${t("Tax (VAT)", "الضريبة")}</td><td class="val">${fmt(invoice.tax)}</td></tr>` : ""}
        <tr class="grand"><td class="lbl">${t("Grand Total", "الإجمالي")}</td><td class="val">${fmt(invoice.total)}</td></tr>
      </table>
    </div>

    <div class="footer">
      <div class="thanks">${escape(isAR ? template?.footer_text_ar : template?.footer_text_en) || ""}</div>
      ${(template?.terms_ar || template?.terms_en) ? `<div class="terms">${escape(isAR ? template?.terms_ar : template?.terms_en)}</div>` : ""}
    </div>

    <div class="actions">
      <button onclick="window.print()">${t("Print", "طباعة")}</button>
    </div>
  </div>
  <script>setTimeout(function(){ window.print(); }, 400);</script>
</body>
</html>
  `;

  const w = window.open("", "_blank", "width=900,height=1200");
  if (!w) return;
  // Using Blob URL avoids document.write — content is rendered as an isolated HTML document
  // Note: invoice data passed via the controlled `html` template literal above; we already
  // escape() user-provided strings (business name, terms, customer name, etc.).
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  w.location.href = url;
  // Revoke URL after the new window has had time to load it
  setTimeout(() => { try { URL.revokeObjectURL(url); } catch {} }, 60_000);
}

function escape(s) {
  if (s == null) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
